import { MenuItem } from '@mui/material';
import classnames from 'classnames';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { MouseEvent, VoidFunctionComponent } from 'react';

import { isSortedByDirection, isSortSelected } from '../../../../../shared/projectsV2';
import * as ProjectsV2Store from '../../../../redux/projectsV2';
import { QualityRequired, SecurityRequired } from '../../../generic';
import Button from '../../../generic/Button';
import Flex from '../../../generic/Flex';
import Icon, { icons } from '../../../generic/Icon';
import { ICON_SIZE } from '../../../generic/Icon/icon-constants';
import { StickyMenu } from '../../../generic/Menu';
import useFetchProjectsAction from '../hooks/useFetchProjectsAction';
import type { ProjectsSort } from '../../../../../shared/projectsV2';
import type { IconClassName } from '../../../generic/Icon';

type Category =
  'issues-total' |
  'issues-licensing' |
  'issues-security' |
  'issues-quality';

type SortCategory = {
  icon: IconClassName;
  label: string;
};

const SORT_CATEGORIES: Readonly<Record<Category, SortCategory>> = {
  'issues-total': {
    icon: icons.globeOutline,
    label: 'Total issues',
  },
  'issues-licensing': {
    icon: icons.fileOutline,
    label: 'Licensing issues',
  },
  'issues-security': {
    icon: icons.shieldHalved,
    label: 'Security issues',
  },
  'issues-quality': {
    icon: icons.sparkle,
    label: 'Quality issues',
  },
};

const IssuesSortingDropdown: VoidFunctionComponent = () => {
  const sort: ProjectsSort = useSelector(ProjectsV2Store.selectors.getSort);
  const setSort = useFetchProjectsAction(ProjectsV2Store.actions.setSort);

  const directionIcon = isSortedByDirection(sort, 'desc') ? icons.angleDown : icons.angleUp;

  const [selectedCategory, setSelectedCategory] = useState<SortCategory>(SORT_CATEGORIES['issues-total']);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);

  // Callbacks
  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggleSort = (category: string) => {
    setSelectedCategory(SORT_CATEGORIES[category as Category]);

    setSort(sort === `${category}_desc` ? `${category}_asc` : `${category}_desc`);
    handleClose();
  };

  // Set category - primarily to set the category on mount
  useEffect(() => {
    const categoryKey = sort.split('_').shift() as Category | 'issues-total';

    // Ensure that we're sorting by a valid issues category
    if (!Object.keys(SORT_CATEGORIES).includes(categoryKey)) {
      setSelectedCategory(SORT_CATEGORIES['issues-total']);
      return;
    }

    setSelectedCategory(SORT_CATEGORIES[categoryKey]);
  }, [sort, setSelectedCategory]);

  return (
    <div>
      <Button
        type="text"
        isFullWidth
        onClick={(e) => handleClick(e)}>
        <div className={classnames('projects-table__header__issues', { 'projects-table__header_active': isSortSelected(sort, 'issues') })}>
          <Flex justify="space-between">
            <span className="projects-table__header__issues__label">
              <Icon icon={selectedCategory.icon} />
              {selectedCategory.label}
            </span>
            {isSortSelected(sort, 'issues') &&
              <Icon icon={directionIcon} size={ICON_SIZE.LG} />}
          </Flex>
        </div>
      </Button>
      <StickyMenu
        id="projects-table-issues-sorting-dropdown"
        anchorEl={anchorEl}
        open={isOpen}
        // MenuListProps={{ style: { padding: '8px' } }}
        popperClassName="projects-dashboard-v2 projects-table projects-table__header projects-table__header__issues"
        paperClassName="projects-table__header__issues__menu"
        onClose={handleClose}
      >
        <MenuItem
          className="projects-table__header__issues__item"
          onClick={() => handleToggleSort('issues-total')}
          selected={isSortSelected(sort, 'issues-total')}
        >
          <Flex justify="space-between" grow={1}>
            <span className="projects-table__header__issues__label">
              <Icon icon={SORT_CATEGORIES['issues-total'].icon} />
              {SORT_CATEGORIES['issues-total'].label}
            </span>
            {isSortSelected(sort, 'issues-total') &&
              <Icon icon={icons.check} />}
          </Flex>
        </MenuItem>
        <MenuItem
          className="projects-table__header__issues__item"
          onClick={() => handleToggleSort('issues-licensing')}
          selected={isSortSelected(sort, 'issues-licensing')}
        >
          <Flex justify="space-between" grow={1}>
            <span className="projects-table__header__issues__label">
              <Icon icon={SORT_CATEGORIES['issues-licensing'].icon} />
              {SORT_CATEGORIES['issues-licensing'].label}
            </span>
            {isSortSelected(sort, 'issues-licensing') &&
              <Icon icon={icons.check} />}
          </Flex>
        </MenuItem>
        <SecurityRequired>
          <MenuItem
            className="projects-table__header__issues__item"
            onClick={() => handleToggleSort('issues-security')}
            selected={isSortSelected(sort, 'issues-security')}
          >
            <Flex justify="space-between" grow={1}>
              <span className="projects-table__header__issues__label">
                <Icon icon={SORT_CATEGORIES['issues-security'].icon} />
                {SORT_CATEGORIES['issues-security'].label}
              </span>
              {isSortSelected(sort, 'issues-security') &&
                <Icon icon={icons.check} />}
            </Flex>
          </MenuItem>
        </SecurityRequired>
        <QualityRequired>
          <MenuItem
            className="projects-table__header__issues__item"
            onClick={() => handleToggleSort('issues-quality')}
            selected={isSortSelected(sort, 'issues-quality')}
          >
            <Flex justify="space-between" grow={1}>
              <span className="projects-table__header__issues__label">
                <Icon icon={SORT_CATEGORIES['issues-quality'].icon} />
                {SORT_CATEGORIES['issues-quality'].label}
              </span>
              {isSortSelected(sort, 'issues-quality') &&
                <Icon icon={icons.check} />}
            </Flex>
          </MenuItem>
        </QualityRequired>
      </StickyMenu>
    </div>
  );
};

export default IssuesSortingDropdown;
